import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Badge, type BadgeTone } from "../../components/ui/Badge";
import { Card } from "../../components/ui/Card";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { SkeletonTable } from "../../components/ui/Skeleton";
import {
	type AuthRole,
	createUser,
	listUsers,
} from "../../lib/auth/authApi";
import { hasRole } from "../../lib/auth/authStore";
import { queryKeys } from "../../lib/queryKeys";

const ROLES: AuthRole[] = ["viewer", "operator", "admin"];

const ROLE_TONE: Record<AuthRole, BadgeTone> = {
	viewer: "muted",
	operator: "info",
	admin: "positive",
};

const ROLE_HINT: Record<AuthRole, string> = {
	viewer: "Read-only — portfolio, balances, previews.",
	operator: "Viewer + simulate / execute transaction bundles.",
	admin: "Operator + manage users.",
};

/** Map the API client's coarse error string to something a human reads. */
function friendlyCreateError(err: unknown): string {
	const msg = err instanceof Error ? err.message : String(err);
	if (/email_taken|\b409\b/.test(msg)) return "That email is already in use.";
	if (/invalid_request|\b400\b/.test(msg)) {
		return "Check the form: a valid email and a password of at least 8 characters are required.";
	}
	if (/\b403\b/.test(msg)) return "Only admins can manage users.";
	return "Could not create the user — please try again.";
}

function formatDate(iso?: string): string {
	if (!iso) return "—";
	const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
	return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * Admin-only user management. Lists existing accounts and provisions new
 * ones (email + password + role). The endpoints are admin-gated server
 * side; this also guards client-side so a non-admin who navigates here
 * directly sees a notice rather than a failed fetch.
 */
export function UsersPanel() {
	const queryClient = useQueryClient();

	const usersQuery = useQuery({
		queryKey: queryKeys.users,
		queryFn: listUsers,
		// Don't even attempt the admin fetch for non-admins.
		enabled: hasRole("admin"),
	});

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [role, setRole] = useState<AuthRole>("viewer");

	const create = useMutation({
		mutationFn: createUser,
		onSuccess: () => {
			setEmail("");
			setPassword("");
			setRole("viewer");
			void queryClient.invalidateQueries({ queryKey: queryKeys.users });
		},
	});

	if (!hasRole("admin")) {
		return (
			<Card tone="warning">
				<p className="text-sm text-amber-200">
					You don't have access to user management.
				</p>
			</Card>
		);
	}

	const onSubmit = (e: FormEvent) => {
		e.preventDefault();
		create.mutate({ email: email.trim(), password, role });
	};

	const users = usersQuery.data ?? [];

	return (
		<div className="space-y-6">
			<SectionHeader
				title="Users"
				subtitle="Provision accounts and assign roles. Admin only."
				trailing={
					<Badge tone="info">{users.length} account{users.length === 1 ? "" : "s"}</Badge>
				}
			/>

			<Card>
				<h3 className="text-sm font-semibold text-slate-100">Add a user</h3>
				<form onSubmit={onSubmit} className="mt-4 space-y-4">
					<div className="grid gap-4 md:grid-cols-3">
						<label className="block text-xs">
							<span className="text-slate-400">Email</span>
							<input
								type="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="person@kam.local"
								className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-600 focus:outline-none"
							/>
						</label>
						<label className="block text-xs">
							<span className="text-slate-400">Password</span>
							<input
								type="password"
								required
								minLength={8}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="at least 8 characters"
								className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-600 focus:outline-none"
							/>
						</label>
						<label className="block text-xs">
							<span className="text-slate-400">Role</span>
							<select
								value={role}
								onChange={(e) => setRole(e.target.value as AuthRole)}
								className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 capitalize focus:border-emerald-600 focus:outline-none"
							>
								{ROLES.map((r) => (
									<option key={r} value={r} className="capitalize">
										{r}
									</option>
								))}
							</select>
						</label>
					</div>

					<p className="text-[11px] text-slate-500">{ROLE_HINT[role]}</p>

					{create.isError && (
						<ErrorBanner>{friendlyCreateError(create.error)}</ErrorBanner>
					)}
					{create.isSuccess && (
						<p className="rounded-md border border-emerald-700/50 bg-emerald-950/30 p-2 text-[11px] text-emerald-300">
							User created.
						</p>
					)}

					<button
						type="submit"
						disabled={create.isPending}
						className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{create.isPending ? "Creating…" : "Create user"}
					</button>
				</form>
			</Card>

			{usersQuery.isLoading ? (
				<SkeletonTable rows={3} cols={3} rowWidths={["w-48", "w-16", "w-20"]} />
			) : usersQuery.isError ? (
				<ErrorBanner>Could not load users.</ErrorBanner>
			) : (
				<div className="overflow-hidden rounded-lg border border-slate-800">
					<table className="w-full text-sm">
						<thead className="bg-slate-900/60 text-left text-xs text-slate-400">
							<tr>
								<th className="px-3 py-2 font-medium">Email</th>
								<th className="px-3 py-2 font-medium">Role</th>
								<th className="px-3 py-2 font-medium">Created</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-800">
							{users.map((u) => (
								<tr key={u.id}>
									<td className="px-3 py-2 text-slate-200">{u.email}</td>
									<td className="px-3 py-2">
										<Badge tone={ROLE_TONE[u.role]}>{u.role}</Badge>
									</td>
									<td className="px-3 py-2 text-slate-400">
										{formatDate(u.createdAt)}
									</td>
								</tr>
							))}
							{users.length === 0 && (
								<tr>
									<td
										colSpan={3}
										className="px-3 py-6 text-center text-xs text-slate-500"
									>
										No users yet.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
